import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TechIcon from './TechIcon';
import './Expertise.css';

/* ────────────────────────────────────────────────────
   macOS ANIMATION PHYSICS

   These replicate the feel of macOS Launchpad:
   - Entry: underdamped spring — visible overshoot
   - Exit: overdamped — snappy, decisive
   - Focus: non-active elements dim + blur
   - Folder: tab lifts, glow spotlight appears
   ──────────────────────────────────────────────────── */

const SPRING_ENTRY = { type: 'spring', stiffness: 170, damping: 13, mass: 0.7 };
const SPRING_EXIT = { type: 'spring', stiffness: 380, damping: 26 };
const SPRING_FOLDER = { type: 'spring', stiffness: 260, damping: 18 };
const EASE_SMOOTH = [0.16, 1, 0.3, 1];

// Pre-set rotations — each icon tumbles slightly on entry
const ROTATIONS = [-14, 10, -8, 12, -10, 8, -12];

const categories = [
  {
    label: 'AI & Intelligence',
    folder: ['#818CF8', '#6366F1'],
    glow: 'rgba(129, 140, 248, 0.4)',
    items: [
      { name: 'LLM', icon: 'llm' },
      { name: 'RAG', icon: 'rag' },
      { name: 'AI Agents', icon: 'agent' },
      { name: 'GPT-4 & Claude', icon: 'gpt' },
      { name: 'pgvector', icon: 'pgvector' },
      { name: 'Orchestration', icon: 'orchestration' },
    ],
    positions: [
      { x: -88, y: -82, s: 1 },
      { x: 14, y: -106, s: 0.92 },
      { x: 98, y: -62, s: 0.86 },
      { x: -104, y: 20, s: 0.88 },
      { x: 100, y: 30, s: 0.84 },
      { x: -6, y: 92, s: 0.94 },
    ],
  },
  {
    label: 'Frontend',
    folder: ['#34D399', '#10B981'],
    glow: 'rgba(52, 211, 153, 0.4)',
    items: [
      { name: 'React', icon: 'react' },
      { name: 'Next.js', icon: 'nextjs' },
      { name: 'Flutter', icon: 'flutter' },
      { name: 'Tailwind', icon: 'tailwind' },
      { name: 'Redux', icon: 'redux' },
      { name: 'PWA', icon: 'pwa' },
    ],
    positions: [
      { x: -94, y: -70, s: 0.92 },
      { x: 20, y: -110, s: 1 },
      { x: 102, y: -56, s: 0.86 },
      { x: -108, y: 24, s: 0.84 },
      { x: 104, y: 34, s: 0.88 },
      { x: 8, y: 96, s: 0.92 },
    ],
  },
  {
    label: 'Backend',
    folder: ['#C084FC', '#A855F7'],
    glow: 'rgba(192, 132, 252, 0.4)',
    items: [
      { name: 'Node.js', icon: 'nodejs' },
      { name: 'Python', icon: 'python' },
      { name: 'PostgreSQL', icon: 'postgresql' },
      { name: 'MongoDB', icon: 'mongodb' },
      { name: 'REST APIs', icon: 'rest' },
      { name: 'GraphQL', icon: 'graphql' },
      { name: 'WebSockets', icon: 'websockets' },
    ],
    positions: [
      { x: -84, y: -90, s: 0.88 },
      { x: 30, y: -112, s: 0.94 },
      { x: 108, y: -50, s: 0.86 },
      { x: -112, y: 14, s: 0.84 },
      { x: 110, y: 20, s: 0.9 },
      { x: -66, y: 86, s: 0.86 },
      { x: 62, y: 92, s: 0.88 },
    ],
  },
  {
    label: 'DevOps',
    folder: ['#FBBF24', '#F59E0B'],
    glow: 'rgba(251, 191, 36, 0.4)',
    items: [
      { name: 'AWS', icon: 'aws' },
      { name: 'Azure', icon: 'cloud' },
      { name: 'Docker', icon: 'docker' },
      { name: 'CI/CD', icon: 'cicd' },
      { name: 'Kubernetes', icon: 'kubernetes' },
      { name: 'Security', icon: 'security' },
    ],
    positions: [
      { x: -92, y: -74, s: 0.92 },
      { x: 16, y: -104, s: 0.86 },
      { x: 100, y: -66, s: 0.9 },
      { x: -102, y: 28, s: 0.86 },
      { x: 104, y: 24, s: 0.84 },
      { x: -4, y: 94, s: 0.88 },
    ],
  },
];

/* ────────────────────────────────────────────
   GLASS FOLDER + PREVIEW ICONS

   Default: Glass folder with 3 raw tech logos
   peeking above it — giving each folder unique
   personality and previewing what's inside.

   On hover: previews fade out, burst takes over.
   ──────────────────────────────────────────── */

// 3 preview logos per category — most recognizable icons
const PREVIEWS = [
  [ // AI
    { icon: 'gpt',           x: -24, y: -32, size: 20, drift: 5.2 },
    { icon: 'agent',         x: 6,   y: -42, size: 17, drift: 6.1 },
    { icon: 'orchestration', x: 28,  y: -28, size: 15, drift: 4.8 },
  ],
  [ // Frontend
    { icon: 'react',   x: -26, y: -34, size: 22, drift: 5.6 },
    { icon: 'nextjs',  x: 4,   y: -44, size: 16, drift: 4.4 },
    { icon: 'flutter', x: 26,  y: -30, size: 18, drift: 5.9 },
  ],
  [ // Backend
    { icon: 'python',     x: -22, y: -36, size: 20, drift: 5.0 },
    { icon: 'nodejs',     x: 8,   y: -46, size: 16, drift: 6.3 },
    { icon: 'postgresql', x: 28,  y: -32, size: 17, drift: 4.6 },
  ],
  [ // Infrastructure
    { icon: 'docker',     x: -24, y: -32, size: 21, drift: 5.4 },
    { icon: 'aws',        x: 6,   y: -44, size: 16, drift: 4.9 },
    { icon: 'kubernetes', x: 28,  y: -28, size: 17, drift: 5.7 },
  ],
];

function GlassFolder({ isActive, catIdx }) {
  const previews = PREVIEWS[catIdx];

  return (
    <div className="gf">
      {/* Preview logos — raw icons floating above folder */}
      {previews.map((p, i) => (
        <motion.div
          key={p.icon}
          className="gf-preview"
          style={{
            left: `calc(50% + ${p.x}px)`,
            top: `calc(50% + ${p.y}px)`,
            '--drift-dur': `${p.drift}s`,
            '--drift-delay': `${i * 0.8}s`,
          }}
          animate={
            isActive
              ? { opacity: 0, scale: 0.4, y: 12 }
              : { opacity: 0.72, scale: 1, y: 0 }
          }
          transition={{ duration: 0.25, ease: EASE_SMOOTH }}
        >
          <TechIcon type={p.icon} size={p.size} />
        </motion.div>
      ))}

      {/* Tab — peeks behind the body, lifts on open */}
      <motion.div
        className="gf-tab"
        animate={isActive ? { y: -8 } : { y: 0 }}
        transition={SPRING_FOLDER}
      />

      {/* Body — main glass surface */}
      <div className={`gf-body ${isActive ? 'gf-body--active' : ''}`}>
        <div className="gf-sheen" />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   BURST ICON — with macOS-quality motion

   Entry: spring overshoot + blur→sharp + rotation settle
   Exit:  snappy spring + blur return + reverse stagger
   Glow:  fades in AFTER icon lands (150ms delay)
   ──────────────────────────────────────────── */
function BurstIcon({ item, position, index, totalItems }) {
  const rot = ROTATIONS[index % ROTATIONS.length];
  const staggerIn = index * 0.045;
  const staggerOut = (totalItems - 1 - index) * 0.025;

  return (
    <motion.div
      className="burst-item"
      initial={{
        x: 0,
        y: 0,
        scale: 0,
        opacity: 0,
        rotate: rot,
        filter: 'blur(10px)',
      }}
      animate={{
        x: position.x,
        y: position.y,
        scale: position.s,
        opacity: 1,
        rotate: 0,
        filter: 'blur(0px)',
      }}
      exit={{
        x: 0,
        y: 0,
        scale: 0,
        opacity: 0,
        rotate: rot * 0.5,
        filter: 'blur(6px)',
        transition: {
          ...SPRING_EXIT,
          delay: staggerOut,
          opacity: { duration: 0.2, delay: staggerOut },
          filter: { duration: 0.2, delay: staggerOut },
        },
      }}
      transition={{
        ...SPRING_ENTRY,
        delay: staggerIn,
        // Opacity and blur resolve quickly, position overshoots
        opacity: { duration: 0.25, ease: 'easeOut', delay: staggerIn },
        filter: { duration: 0.35, ease: 'easeOut', delay: staggerIn },
      }}
    >
      <div className="burst-circle">
        <TechIcon type={item.icon} size={22} />
      </div>

      {/* Label — appears last, after icon + glow */}
      <motion.span
        className="burst-label"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, transition: { duration: 0.1 } }}
        transition={{
          duration: 0.35,
          ease: EASE_SMOOTH,
          delay: staggerIn + 0.28,
        }}
      >
        {item.name}
      </motion.span>
    </motion.div>
  );
}

/* ────────────────────────────────────────────
   MAIN COMPONENT
   ──────────────────────────────────────────── */
export default function Expertise() {
  const [active, setActive] = useState(null);

  const handleEnter = useCallback((idx) => setActive(idx), []);
  const handleLeave = useCallback(() => setActive(null), []);
  const handleTap = useCallback(
    (idx) => setActive((prev) => (prev === idx ? null : idx)),
    []
  );

  return (
    <section className="expertise-section" id="about">
      <div className="expertise-inner">
        <motion.div
          className="expertise-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8, ease: EASE_SMOOTH }}
        >
          <p className="section-label">Tech Stack</p>
        </motion.div>

        <div className="folders-row">
          {categories.map((cat, catIdx) => {
            const isActive = active === catIdx;
            const isSiblingActive = active !== null && active !== catIdx;

            return (
              <div
                key={cat.label}
                className={`folder-slot ${isActive ? 'folder-slot--active' : ''}`}
                onMouseEnter={() => handleEnter(catIdx)}
                onMouseLeave={handleLeave}
                onClick={() => handleTap(catIdx)}
              >
                {/* Soft ambient light behind active folder */}
                <motion.div
                  className="folder-spotlight"
                  style={{ background: `radial-gradient(circle, rgba(0,0,0,0.03), transparent 70%)` }}
                  animate={
                    isActive
                      ? { opacity: 1, scale: 1.2 }
                      : { opacity: 0, scale: 0.6 }
                  }
                  transition={{ duration: 0.5, ease: EASE_SMOOTH }}
                />

                {/* Folder icon — lifts when active, dims when sibling is active */}
                <motion.div
                  className="folder-icon"
                  animate={{
                    scale: isActive ? 1.14 : isSiblingActive ? 0.88 : 1,
                    y: isActive ? -10 : 0,
                    opacity: isSiblingActive ? 0.28 : 1,
                    filter: isSiblingActive ? 'blur(2px)' : 'blur(0px)',
                  }}
                  transition={{
                    ...SPRING_FOLDER,
                    opacity: { duration: 0.35, ease: EASE_SMOOTH },
                    filter: { duration: 0.35, ease: EASE_SMOOTH },
                  }}
                >
                  <GlassFolder isActive={isActive} catIdx={catIdx} />
                </motion.div>

                {/* Label — brightens when active, fades when sibling active */}
                <motion.span
                  className="folder-label"
                  animate={{
                    opacity: isActive ? 1 : isSiblingActive ? 0.18 : 0.55,
                    y: isActive ? 3 : 0,
                    filter: isSiblingActive ? 'blur(1px)' : 'blur(0px)',
                  }}
                  transition={{ duration: 0.35, ease: EASE_SMOOTH }}
                >
                  {cat.label}
                </motion.span>

                {/* Burst field — icons radiate from folder center */}
                <AnimatePresence mode="wait">
                  {isActive && (
                    <div className="burst-field">
                      {cat.items.map((item, itemIdx) => (
                        <BurstIcon
                          key={item.name}
                          item={item}
                          position={cat.positions[itemIdx]}
                          index={itemIdx}
                          totalItems={cat.items.length}
                        />
                      ))}
                    </div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
